import { DateTime } from 'luxon';

import type { CalDavCalendar } from '@/features/caldav/types';
import { colorStore } from '@/features/queueColors';
import type { WeeklyWorkLog } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import type { CalendarEventWithColor, CollapsedLogGroup, WeekViewData } from '../../types';
import { buildWeekViewData } from '../../utils/eventLayout';

/** Начало рабочего диапазона для умного размещения (08:00) */
const SLOT_RANGE_START_MIN = 8 * 60;
/** Конец рабочего диапазона для умного размещения (20:00) */
const SLOT_RANGE_END_MIN = 20 * 60;

/**
 * Модель для позиционирования "other logs" на календарной сетке.
 *
 * "Other logs" — это worklogs из Яндекс.Трекера, которые НЕ привязаны
 * к CalDAV событиям (залогированы напрямую, без календаря).
 *
 * Основные задачи:
 * - Найти worklogs, не сопоставленные с событиями календаря
 * - Преобразовать их в pseudo-events для отображения на сетке
 * - Умно распределить логи с дефолтным временем 12:00 по свободным слотам
 * - Сгруппировать пересекающиеся логи и показать карточку "ещё N"
 */
export class OtherLogsLayoutModel {
  /**
   * Находит worklogs, не привязанные ни к одному CalDAV событию.
   * Сравнивает worklogId событий с ID загруженных worklogs.
   */
  computeUnmatchedWorklogs(
    weekData: WeekViewData | null,
    weeklyWorklogs: Map<string, WeeklyWorkLog>,
  ): WeeklyWorkLog[] {
    if (!weekData || weeklyWorklogs.size === 0) return [];

    const matchedIds = new Set<string>();

    for (const day of weekData.days) {
      for (const pe of day.timedEvents) {
        if (pe.event.worklogId) matchedIds.add(pe.event.worklogId);
      }

      for (const e of day.allDayEvents) {
        if (e.worklogId) matchedIds.add(e.worklogId);
      }
    }

    const result: WeeklyWorkLog[] = [];

    for (const [id, wl] of weeklyWorklogs.entries()) {
      if (matchedIds.has(id)) continue;

      const localStart = DateTime.fromISO(wl.start).toLocal();

      result.push({
        ...wl,
        comment: wl.comment || undefined,
        date: localStart.toISODate() ?? '',
        start: localStart.toISO()!,
      });
    }

    return result;
  }

  /**
   * Создаёт WeekViewData с добавленными other logs как pseudo-events.
   * Логи с дефолтным временем 12:00 перемещаются в свободные слоты.
   */
  buildWeekDataWithOtherLogs(
    baseWeekData: WeekViewData,
    worklogs: WeeklyWorkLog[],
    calendars: CalDavCalendar[],
  ): WeekViewData {
    if (worklogs.length === 0) return baseWeekData;

    const { repositioned, collapsedCards } = this._repositionNoonWorklogs(worklogs, baseWeekData);

    const allEvents: CalendarEventWithColor[] = [];

    for (const day of baseWeekData.days) {
      for (const pe of day.timedEvents) {
        allEvents.push(pe.event);
      }

      for (const e of day.allDayEvents) {
        allEvents.push(e);
      }
    }

    const pseudoEvents = this._buildOtherLogPseudoEvents(baseWeekData, repositioned);

    return buildWeekViewData(
      [...allEvents, ...pseudoEvents, ...collapsedCards],
      calendars,
      baseWeekData.weekStart,
    );
  }

  /**
   * Преобразует worklogs в pseudo-events для отображения на сетке.
   * Группирует пересекающиеся логи и создаёт collapsed-карточки для избытка.
   */
  private _buildOtherLogPseudoEvents(
    baseWeekData: WeekViewData,
    worklogs: WeeklyWorkLog[],
  ): CalendarEventWithColor[] {
    if (worklogs.length === 0) return [];

    const byDate = new Map<string, WeeklyWorkLog[]>();

    for (const wl of worklogs) {
      const existing = byDate.get(wl.date) ?? [];
      existing.push(wl);
      byDate.set(wl.date, existing);
    }

    const pseudoEvents: CalendarEventWithColor[] = [];

    for (const [date, dateLogs] of byDate.entries()) {
      const sorted = [...dateLogs].sort(
        (a, b) => DateTime.fromISO(a.start).toMillis() - DateTime.fromISO(b.start).toMillis(),
      );

      const collisionGroups = this._findLogCollisionGroups(sorted);

      for (const logGroup of collisionGroups) {
        const dayStart = DateTime.fromISO(date).startOf('day');
        const groupStart = DateTime.fromISO(logGroup[0].start);
        const startMinutes = groupStart.diff(dayStart, 'minutes').minutes;

        const calendarEventsInSlot = this._countCalendarEventsInSlot(baseWeekData, date, logGroup);

        const maxVisible = Math.max(0, 4 - calendarEventsInSlot - 1);

        let visibleLogs: WeeklyWorkLog[];
        let collapsedLogs: WeeklyWorkLog[];

        if (logGroup.length <= maxVisible + 1) {
          visibleLogs = logGroup;
          collapsedLogs = [];
        } else {
          visibleLogs = logGroup.slice(0, maxVisible);
          collapsedLogs = logGroup.slice(maxVisible);
        }

        for (const wl of visibleLogs) {
          const keyPrefix = wl.taskKey.split('-')[0];
          const color = colorStore.getColorByPrefix(keyPrefix);
          const wlStart = DateTime.fromISO(wl.start);
          const wlEnd = wlStart.plus({ minutes: wl.durationMinutes });

          pseudoEvents.push({
            id: `other-log-${wl.id}`,
            calendarId: '__other_logs__',
            externalId: '',
            recurrenceId: null,
            summary: wl.taskKey,
            description: wl.comment || null,
            startsAt: wlStart.toISO()!,
            endsAt: wlEnd.toISO()!,
            isAllDay: false,
            location: null,
            worklogId: wl.id,
            myResponseStatus: null,
            attendees: [],
            color,
            isOtherLog: true,
            otherLogData: wl,
          });
        }

        if (collapsedLogs.length > 0) {
          const minDuration = Math.min(...collapsedLogs.map((l) => l.durationMinutes));
          const totalDuration = collapsedLogs.reduce((sum, l) => sum + l.durationMinutes, 0);
          const collapsedStart = dayStart.plus({ minutes: startMinutes });
          const collapsedEnd = collapsedStart.plus({ minutes: Math.max(minDuration, 30) });

          const group: CollapsedLogGroup = {
            logs: logGroup,
            visibleLogs,
            collapsedLogs,
            minCollapsedDuration: minDuration,
            totalCollapsedDuration: totalDuration,
            date,
            startMinutes,
          };

          pseudoEvents.push({
            id: `collapsed-${date}-${startMinutes}`,
            calendarId: '__other_logs__',
            externalId: '',
            recurrenceId: null,
            summary: `ещё ${collapsedLogs.length}`,
            description: null,
            startsAt: collapsedStart.toISO()!,
            endsAt: collapsedEnd.toISO()!,
            isAllDay: false,
            location: null,
            worklogId: null,
            myResponseStatus: null,
            attendees: [],
            color: '#bdbdbd',
            isOtherLog: true,
            collapsedGroup: group,
          });
        }
      }
    }

    return pseudoEvents;
  }

  /**
   * Группирует логи по временным пересечениям.
   * Логи, чьи временные интервалы накладываются, попадают в одну группу.
   */
  private _findLogCollisionGroups(sortedLogs: WeeklyWorkLog[]): WeeklyWorkLog[][] {
    const groups: WeeklyWorkLog[][] = [];
    let currentGroup: WeeklyWorkLog[] = [];
    let groupEnd = DateTime.fromMillis(0);

    for (const log of sortedLogs) {
      const logStart = DateTime.fromISO(log.start);
      const logEnd = logStart.plus({ minutes: log.durationMinutes });

      if (currentGroup.length === 0 || logStart < groupEnd) {
        currentGroup.push(log);

        if (logEnd > groupEnd) groupEnd = logEnd;
      } else {
        groups.push(currentGroup);
        currentGroup = [log];
        groupEnd = logEnd;
      }
    }

    if (currentGroup.length > 0) groups.push(currentGroup);

    return groups;
  }

  /** Считает количество CalDAV событий, пересекающихся с группой логов. */
  private _countCalendarEventsInSlot(
    weekData: WeekViewData | null,
    date: string,
    logGroup: WeeklyWorkLog[],
  ): number {
    if (!weekData) return 0;

    const dayColumn = weekData.days.find((d) => d.date.toISODate() === date);

    if (!dayColumn) return 0;

    const groupStart = DateTime.fromISO(logGroup[0].start);
    const groupEnd = logGroup.reduce((end, log) => {
      const logEnd = DateTime.fromISO(log.start).plus({ minutes: log.durationMinutes });

      return logEnd > end ? logEnd : end;
    }, groupStart);

    let count = 0;

    for (const pe of dayColumn.timedEvents) {
      const eventStart = DateTime.fromISO(pe.event.startsAt);
      const eventEnd = DateTime.fromISO(pe.event.endsAt);

      if (eventStart < groupEnd && eventEnd > groupStart) {
        count++;
      }
    }

    return count;
  }

  /**
   * Перемещает логи с дефолтным временем 12:00 в свободные слоты.
   *
   * При логировании без указания времени Трекер ставит 12:00.
   * Этот метод распределяет такие логи по свободным промежуткам
   * между залогированными событиями в диапазоне 08:00–20:00.
   *
   * Логи, для которых не нашлось места, возвращаются как collapsed-карточки.
   */
  private _repositionNoonWorklogs(
    worklogs: WeeklyWorkLog[],
    baseWeekData: WeekViewData,
  ): { repositioned: WeeklyWorkLog[]; collapsedCards: CalendarEventWithColor[] } {
    const byDate = new Map<string, WeeklyWorkLog[]>();

    for (const wl of worklogs) {
      const existing = byDate.get(wl.date) ?? [];
      existing.push(wl);
      byDate.set(wl.date, existing);
    }

    const repositioned: WeeklyWorkLog[] = [];
    const collapsedCards: CalendarEventWithColor[] = [];

    for (const [date, dateLogs] of byDate.entries()) {
      const noonLogs: WeeklyWorkLog[] = [];
      const positionedLogs: WeeklyWorkLog[] = [];

      for (const wl of dateLogs) {
        const wlStart = DateTime.fromISO(wl.start);

        if (wlStart.hour === 12 && wlStart.minute === 0) {
          noonLogs.push(wl);
        } else {
          positionedLogs.push(wl);
        }
      }

      repositioned.push(...positionedLogs);

      if (noonLogs.length === 0) continue;

      const occupied = this._buildOccupiedIntervals(baseWeekData, date);

      const dayStart = DateTime.fromISO(date).startOf('day');

      for (const wl of positionedLogs) {
        const wlStart = DateTime.fromISO(wl.start);
        const startMin = wlStart.diff(dayStart, 'minutes').minutes;
        occupied.push({ start: startMin, end: startMin + wl.durationMinutes });
      }

      occupied.sort((a, b) => a.start - b.start);

      const freeSlots = OtherLogsLayoutModel._buildFreeIntervals(
        occupied,
        SLOT_RANGE_START_MIN,
        SLOT_RANGE_END_MIN,
      );

      const unplaced: WeeklyWorkLog[] = [];

      for (const wl of noonLogs) {
        const slotStart = OtherLogsLayoutModel._findAndConsumeSlot(freeSlots, wl.durationMinutes);

        if (slotStart !== null) {
          const newStart = dayStart.plus({ minutes: slotStart });
          repositioned.push({ ...wl, start: newStart.toISO()!, originalStart: wl.start });
        } else {
          unplaced.push(wl);
        }
      }

      if (unplaced.length > 0) {
        const totalDuration = unplaced.reduce((sum, l) => sum + l.durationMinutes, 0);

        const group: CollapsedLogGroup = {
          logs: unplaced,
          visibleLogs: [],
          collapsedLogs: unplaced,
          minCollapsedDuration: Math.min(...unplaced.map((l) => l.durationMinutes)),
          totalCollapsedDuration: totalDuration,
          date,
          startMinutes: 0,
        };

        collapsedCards.push({
          id: `collapsed-unplaced-${date}`,
          calendarId: '__other_logs__',
          externalId: '',
          recurrenceId: null,
          summary: `ещё ${unplaced.length} · +${formatDurationRussian(totalDuration)}`,
          description: null,
          startsAt: date,
          endsAt: date,
          isAllDay: true,
          location: null,
          worklogId: null,
          myResponseStatus: null,
          attendees: [],
          color: '#bdbdbd',
          isOtherLog: true,
          collapsedGroup: group,
        });
      }
    }

    return { repositioned, collapsedCards };
  }

  /**
   * Строит список занятых интервалов (в минутах от полуночи)
   * на основе залогированных CalDAV событий.
   */
  private _buildOccupiedIntervals(
    weekData: WeekViewData,
    date: string,
  ): Array<{ start: number; end: number }> {
    const dayColumn = weekData.days.find((d) => d.date.toISODate() === date);

    if (!dayColumn) return [];

    const dayStart = DateTime.fromISO(date).startOf('day');
    const intervals: Array<{ start: number; end: number }> = [];

    for (const pe of dayColumn.timedEvents) {
      if (!pe.event.matchedWorklog || pe.event.matchedWorklog.duration <= 0) continue;

      const evStart = DateTime.fromISO(pe.event.startsAt);
      const evEnd = DateTime.fromISO(pe.event.endsAt);
      const startMin = Math.max(0, evStart.diff(dayStart, 'minutes').minutes);
      const endMin = Math.max(0, evEnd.diff(dayStart, 'minutes').minutes);

      if (endMin > startMin) {
        intervals.push({ start: startMin, end: endMin });
      }
    }

    return intervals;
  }

  /**
   * Вычисляет свободные интервалы как промежутки между занятыми.
   * Объединяет пересекающиеся занятые интервалы перед расчётом.
   */
  private static _buildFreeIntervals(
    occupied: Array<{ start: number; end: number }>,
    rangeStart: number,
    rangeEnd: number,
  ): Array<{ start: number; end: number }> {
    const merged: Array<{ start: number; end: number }> = [];

    for (const interval of occupied) {
      const s = Math.max(interval.start, rangeStart);
      const e = Math.min(interval.end, rangeEnd);

      if (s >= e) continue;

      if (merged.length > 0 && s <= merged[merged.length - 1].end) {
        merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, e);
      } else {
        merged.push({ start: s, end: e });
      }
    }

    const free: Array<{ start: number; end: number }> = [];
    let cursor = rangeStart;

    for (const m of merged) {
      if (cursor < m.start) {
        free.push({ start: cursor, end: m.start });
      }

      cursor = Math.max(cursor, m.end);
    }

    if (cursor < rangeEnd) {
      free.push({ start: cursor, end: rangeEnd });
    }

    return free;
  }

  /**
   * Находит первый свободный слот достаточной длительности и "потребляет" его.
   * Возвращает начало слота в минутах или null, если места нет.
   */
  private static _findAndConsumeSlot(
    freeIntervals: Array<{ start: number; end: number }>,
    durationMinutes: number,
  ): number | null {
    for (let i = 0; i < freeIntervals.length; i++) {
      const interval = freeIntervals[i];
      const available = interval.end - interval.start;

      if (available >= durationMinutes) {
        const slotStart = interval.start;
        interval.start += durationMinutes;

        if (interval.start >= interval.end) {
          freeIntervals.splice(i, 1);
        }

        return slotStart;
      }
    }

    return null;
  }
}
